<template>
  <div class="ev-simulate-page">
    <div class="page-header">
      <div class="header-main">
        <!-- 推导 -->
        <div class="header-block derive-block">
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="开始日期">
              <el-date-picker v-model="param.startDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 130px" :disabled="running"></el-date-picker>
            </el-form-item>
            <el-form-item label="天数">
              <el-input-number v-model="param.days" :min="1" :max="7" :controls="false" style="width: 48px"
                :disabled="running"></el-input-number>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" size="mini" :loading="running" @click="startSimulate">初始化</el-button>
              <el-button v-if="running" type="danger" size="mini" :loading="stopping"
                @click="forceStopSimulate">强制停止</el-button>
              <el-dropdown ref="moreDropdown" trigger="click" :hide-on-click="false" @command="onMoreCommand">
                <el-button size="mini" class="more-btn" :class="{ 'is-auto': opsTestAuto }">
                  更多<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="isDev" command="opsTest">操作测试</el-dropdown-item>
                  <el-dropdown-item v-if="isDev" command="noop" class="ops-auto-item">
                    <span>自动测试</span>
                    <el-switch v-model="opsTestAuto" @change="onOpsTestAutoChange" @click.native.stop></el-switch>
                  </el-dropdown-item>
                  <el-dropdown-item command="config">配置</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-form-item>
          </el-form>
        </div>

        <!-- 求解 EV 概要（居中） -->
        <div class="ev-block-wrap" v-if="hasSolveMetrics">
          <el-tooltip :content="evMetricTip" placement="bottom">
            <div class="ev-block">
              <div class="ev-metric">
                <span class="ev-metric-label">收工EV（全局）</span>
                <span class="ev-metric-value">{{ formatEvNum(globalStopWorkEv) }}</span>
              </div>
              <div class="ev-metric" v-if="filterPathId">
                <span class="ev-metric-label">收工EV（本区域）</span>
                <span class="ev-metric-value muted">{{ formatEvNum(areaStopWorkEv) }}</span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">缺票惩罚</span>
                <span class="ev-metric-value" :title="slackPenaltyTip">{{ formatEvNum(slackPenaltyValue) }}<span
                    v-if="slackSeatCount" class="ev-metric-unit">（{{ slackSeatCount }}座）</span></span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">D+1惩罚</span>
                <span class="ev-metric-value">{{ formatEvNum(penaltyValue) }}</span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">路线成本</span>
                <span class="ev-metric-value">{{ formatEvNum(tripPenaltyValue) }}</span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">放空惩罚</span>
                <span class="ev-metric-value">{{ formatEvNum(unusedCarPenaltyValue) }}</span>
              </div>
              <div class="ev-metric">
                <span class="ev-metric-label">可售收益</span>
                <span class="ev-metric-value">{{ formatEvNum(sellableRewardValue) }}</span>
              </div>
              <div class="ev-metric is-objective" :class="{ 'is-mismatch': evObjectiveMismatch }">
                <span class="ev-metric-label">最终EV（净值）</span>
                <span class="ev-metric-value">{{ formatEvNum(objectiveValue) }}</span>
              </div>
              <div class="ev-metric" :class="solveQualityTone">
                <span class="ev-metric-label">求解质量</span>
                <span class="ev-metric-value" :title="solveQualityTip">
                  {{ solveQualityStatus }}
                  <span class="ev-metric-gap" v-if="solveQualityGap">{{ solveQualityGap }}</span>
                </span>
              </div>
              <div class="ev-metric is-unassigned" v-if="unarrangedOrderCount > 0">
                <span class="ev-metric-label">未安排</span>
                <span class="ev-metric-value" :title="unarrangedTip">{{ unarrangedOrderCount }}单</span>
              </div>
              <div class="ev-metric is-reconcile" v-if="reconciledCount > 0 || reconcileFailedCount > 0">
                <span class="ev-metric-label">求解后补挂</span>
                <span class="ev-metric-value" :title="reconciledTip">{{ reconciledCount }}单</span>
              </div>
            </div>
          </el-tooltip>
        </div>
        <div class="ev-block-wrap" v-else>
          <div class="ev-block ev-placeholder">
            <span class="ev-placeholder-text">完成 EV 推导后在此显示求解概要</span>
          </div>
        </div>

        <!-- 查看筛选 -->
        <div class="header-block view-block">
          <div class="block-label">查看</div>
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="路线">
              <el-select v-model="filterPathId" placeholder="全部路线" clearable style="width: 120px">
                <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="日期">
              <el-date-picker v-model="viewDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 130px"></el-date-picker>
            </el-form-item>
            <el-form-item>
              <el-button size="mini" :loading="refreshing" @click="onRefresh">刷新</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <ev-epoch-dashboard :date="viewDate" :has-ledger="!!dayView" :ledger-status="solveStatus"
        :assigned-person="filteredAssignedPerson" :unassigned-person="filteredUnassignedPerson"
        :trip-count="filteredTripCount" :derive-at="solveExecutedAt" :derive-operator="solveOperator"
        :solve-time-text="solveTimeText" :has-extra="hasTaskStatus" :init-started-at="initStartedAt"
        :init-finished-at="initFinishedAt" @applied="onRefresh" @epoch-change="onEpochChange">
        <ev-init-day-wheel slot="wheel" :items="wheelItems" :active-date="viewDate" @select-date="onWheelSelectDate" />
        <div v-if="hasTaskStatus && task && task.status === 2" slot="extra" class="task-status">
          <span class="toolbar__error" :title="task.errorMsg" @click.stop="showTaskError">{{ task.errorMsg }}</span>
        </div>
      </ev-epoch-dashboard>
    </div>

    <div class="main-content">
      <div class="tabs-toolbar">
        <el-input v-model="searchTel" class="tel-search" size="mini" clearable placeholder="手机号搜索高亮"
          prefix-icon="el-icon-search" />
      </div>
      <el-tabs v-model="tab" class="fill-tabs">
        <el-tab-pane name="unassigned">
          <span slot="label">已订（{{ filteredUnassignedPerson }}人）</span>
          <unassigned-order ref="unassignedRef" :list="filteredUnassignedOrders" :date="viewDate"
            :table-height="tableMaxHeight" :highlight-tel="searchTel" :unarranged-order-ids="unarrangedOrderIds"
            @assign="handleAssignOrder" />
        </el-tab-pane>

        <el-tab-pane name="dispatched">
          <span slot="label">已派（{{ filteredAssignedPerson }}人）</span>
          <simulated-dispatched-order ref="dispatchedRef" :trips="filteredDispatchedTrips"
            :table-height="tableMaxHeight" :highlight-tel="searchTel" :reconciled-order-ids="reconciledOrderIds"
            @revoke="handleRevokeOrder" @reassign="handleReassignOrder" />
        </el-tab-pane>

        <el-tab-pane name="idle">
          <span slot="label">可售余票</span>
          <idle-vehicle-panel v-if="filterPathId" ref="idleRef" :rows="sellableSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看可售余票</div>
        </el-tab-pane>

        <el-tab-pane name="opAreaHour">
          <span slot="label">运营指标</span>
          <op-area-hour-panel v-if="filterPathId" :rows="opAreaHourSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看运营层数据</div>
        </el-tab-pane>

        <el-tab-pane name="vehicleInit">
          <span slot="label">日初状态（{{ vehicleInitCount }}人）</span>
          <vehicle-tip-panel :rows="vehicleInitRows" :view-date="viewDate" :table-height="tableMaxHeight"
            :loading="refreshing" />
        </el-tab-pane>

        <el-tab-pane name="vehicleFinal">
          <span slot="label">日末状态（{{ vehicleFinalCount }}人）</span>
          <vehicle-tip-panel :rows="vehicleFinalRows" :view-date="viewDate" :table-height="tableMaxHeight"
            :loading="refreshing" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <assign-driver-dialog ref="assignDialog" @success="onAssignSuccess" />
    <ev-ops-test-dialog ref="opsTestDialog" @done="onOpsTestDone" />
  </div>
</template>

<script>
import {
  submitEvSimulate,
  getEvSimulateTask,
  getEvRunningSimulate,
  forceStopEvSimulate,
  getEvSolverHorizon,
  getEvPlanView,
  getEvSellableView,
  getEvOpAreaHourView,
  getEvSolveSummaryView,
  getEvVehicleFinalView,
  getEvVehicleInitView,
  revokeEvOrder
} from "@/api/ev_solver";
import { DateUtil } from "@/util";
import { mapState } from "vuex";
import config from "@/config/config";
import UnassignedOrder from "./components/simulate/UnassignedOrder";
import SimulatedDispatchedOrder from "./components/simulate/SimulatedDispatchedOrder";
import IdleVehiclePanel from "./components/simulate/IdleVehiclePanel";
import OpAreaHourPanel from "./components/simulate/OpAreaHourPanel";
import VehicleTipPanel from "./components/simulate/VehicleTipPanel";
import AssignDriverDialog from "./components/simulate/AssignDriverDialog";
import EvEpochDashboard from "./components/simulate/EvEpochDashboard";
import EvOpsTestDialog from "./components/simulate/EvOpsTestDialog";
import EvInitDayWheel from "./components/simulate/EvInitDayWheel";

const RUNNING_TASK_KEY = "evSimulate.runningTaskId";

/** 各页签数据缓存，切换页签/筛选条件时只读缓存，不自动请求 */
function createViewCache() {
  return {
    plan: {},
    sellable: {},
    opAreaHour: {},
    vehicleFinal: {},
    vehicleInit: {},
    solveSummary: {}
  };
}

export default {
  components: {
    UnassignedOrder,
    SimulatedDispatchedOrder,
    IdleVehiclePanel,
    OpAreaHourPanel,
    VehicleTipPanel,
    AssignDriverDialog,
    EvEpochDashboard,
    EvOpsTestDialog,
    EvInitDayWheel
  },

  data() {
    return {
      tab: "dispatched",
      searchTel: "",
      viewDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
      filterPathId: null,
      dayView: null,
      sellableSnapshots: [],
      opAreaHourSnapshots: [],
      solveSummary: null,
      viewCache: createViewCache(),
      refreshing: false,
      lastLoadedQueryKey: null,
      param: {
        startDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
        days: 7
      },
      taskId: null,
      task: null,
      running: false,
      stopping: false,
      getResulted: true,
      pollTimer: null,
      horizonTimer: null,
      horizonItems: [],
      /** 当前查看日的 epoch 实时状态，轮盘与左侧「搜索中」共用 */
      liveEpochByDate: {},
      /** 各 tab 表格 max-height（px），按页签可视高度计算，只保留表体内部滚动 */
      tableMaxHeight: 480,
      opsTestAuto: false,
      opsTestAutoTimer: null,
    };
  },

  computed: {
    ...mapState({
      pathOpts: (state) => state.pathList
    }),

    isDev() {
      return config.isDev;
    },

    pathCacheKey() {
      if (!this.viewDate || !this.filterPathId) {
        return null;
      }
      return `${this.viewDate}_${this.filterPathId}`;
    },

    queryKey() {
      return `${this.viewDate || ""}_${this.filterPathId || ""}`;
    },

    solveStatus() {
      const summary = this.dayView && this.dayView.solveSummary;
      return (summary && summary.status) || "-";
    },

    areaStopWorkEv() {
      return (this.opAreaHourSnapshots || []).reduce((sum, row) => sum + (row.evObjective || 0), 0);
    },

    globalStopWorkEv() {
      const s = this.solveSummary;
      return s && s.stopWorkEv != null ? s.stopWorkEv : null;
    },

    penaltyValue() {
      const s = this.solveSummary;
      return s && s.penalty != null ? s.penalty : null;
    },

    slackPenaltyValue() {
      const s = this.solveSummary;
      return s && s.slackPenalty != null ? s.slackPenalty : null;
    },

    tripPenaltyValue() {
      const s = this.solveSummary;
      return s && s.tripCostTotal != null ? s.tripCostTotal : null;
    },

    unusedCarPenaltyValue() {
      const s = this.solveSummary;
      return s && s.unusedCarCostTotal != null ? s.unusedCarCostTotal : null;
    },

    sellableRewardValue() {
      const s = this.solveSummary;
      return s && s.sellableReward != null ? s.sellableReward : null;
    },

    objectiveValue() {
      const s = this.solveSummary;
      return s && s.objective != null ? s.objective : null;
    },

    /** 收工EV − 各项惩罚 + 可售收益，与页面展示口径一致 */
    reconciledObjectiveValue() {
      const stop = this.globalStopWorkEv;
      if (stop == null) {
        return this.objectiveValue;
      }
      return stop
        - (this.slackPenaltyValue || 0)
        - (this.penaltyValue || 0)
        - (this.tripPenaltyValue || 0)
        - (this.unusedCarPenaltyValue || 0)
        + (this.sellableRewardValue || 0);
    },

    evObjectiveMismatch() {
      if (this.globalStopWorkEv == null || this.objectiveValue == null) {
        return false;
      }
      return Math.abs(this.reconciledObjectiveValue - this.objectiveValue) > 0.05;
    },

    evMetricTip() {
      const parts = ["净值 = 收工EV − 缺票惩罚 − D+1惩罚 − 行程惩罚 − 闲置整车 + 可售收益（本小时发车余座的前 4 座，收益逐座减半）"];
      parts.push("缺票按求解桶座计数，与是否挂上班无关");
      if (this.solveQualityTip) {
        parts.push(this.solveQualityTip);
      }
      if (this.evObjectiveMismatch) {
        parts.push("当前对不上：按展示项相减=" + this.formatEvNum(this.reconciledObjectiveValue)
          + "，求解器净值=" + this.formatEvNum(this.objectiveValue));
      }
      return parts.join("。");
    },

    slackSeatCount() {
      const penalty = this.slackPenaltyValue;
      if (penalty == null || penalty <= 0) {
        return 0;
      }
      return Math.round(penalty / 8000);
    },

    solverUnassignedOrderIds() {
      const s = this.solveSummary;
      return (s && s.solverUnassignedOrderIds) || [];
    },

    reconciledOrderIds() {
      const s = this.solveSummary;
      return (s && s.reconciledOrderIds) || [];
    },

    reconcileFailedOrderIds() {
      const s = this.solveSummary;
      return (s && s.reconcileFailedOrderIds) || [];
    },

    reconciledCount() {
      return this.reconciledOrderIds.length;
    },

    reconcileFailedCount() {
      return this.reconcileFailedOrderIds.length;
    },

    slackPenaltyTip() {
      const parts = [];
      if (this.slackSeatCount) {
        parts.push("求解缺" + this.slackSeatCount + "座（每座8000）");
      }
      if (this.unarrangedOrderIds.length) {
        parts.push("求解未安排：" + this.unarrangedOrderIds.join("、"));
      }
      parts.push("缺票是座位缺口，不按订单打标");
      return parts.join("；");
    },

    unarrangedOrderIds() {
      const currentIds = ((this.dayView && this.dayView.unassignedOrders) || [])
        .map((order) => (order && order.id != null ? order.id : null))
        .filter((id) => id != null);
      if (!this.solverUnassignedOrderIds.length) {
        return currentIds;
      }
      const current = {};
      currentIds.forEach((id) => {
        current[String(id)] = true;
      });
      return this.solverUnassignedOrderIds.filter((id) => id != null && current[String(id)]);
    },

    unarrangedOrderCount() {
      return this.unarrangedOrderIds.length;
    },

    unarrangedTip() {
      const parts = ["求解器没有安排到任何班次，仍在已订"];
      if (this.unarrangedOrderIds.length) {
        parts.push("订单：" + this.unarrangedOrderIds.join("、"));
      }
      return parts.join("。");
    },

    reconciledTip() {
      const parts = [];
      if (this.reconciledCount) {
        parts.push("补挂成功：" + this.reconciledOrderIds.join("、"));
      }
      if (this.reconcileFailedCount) {
        parts.push("仍未挂上：" + this.reconcileFailedOrderIds.join("、"));
      }
      return parts.join("；");
    },

    hasSolveMetrics() {
      return this.globalStopWorkEv != null
        || this.slackPenaltyValue != null
        || this.penaltyValue != null
        || this.tripPenaltyValue != null
        || this.unusedCarPenaltyValue != null
        || this.sellableRewardValue != null
        || this.objectiveValue != null;
    },

    solveQualityStatus() {
      const s = this.solveSummary;
      return (s && s.status) || "-";
    },

    solveQualityGap() {
      const s = this.solveSummary;
      return s ? this.formatGap(s.relativeGap) : "";
    },

    solveQualityText() {
      const status = this.solveQualityStatus;
      const gap = this.solveQualityGap;
      return gap ? `${status} ${gap}` : status;
    },

    solveQualityTone() {
      const status = this.solveSummary && this.solveSummary.status;
      if (status === "OPTIMAL") {
        return "is-ok";
      }
      if (status === "FEASIBLE") {
        return "is-warn";
      }
      return "";
    },

    solveQualityTip() {
      const s = this.solveSummary;
      if (!s) {
        return "";
      }
      const parts = [];
      if (s.status === "OPTIMAL") {
        parts.push("OPTIMAL：已证明最优，放空若仍出现是模型在奖励它");
      } else if (s.status === "FEASIBLE") {
        parts.push("FEASIBLE：只是可行解，未证明最优，优先怀疑时间/gap");
      }
      if (s.relativeGap != null && !Number.isNaN(s.relativeGap)) {
        parts.push("相对gap=" + this.formatGap(s.relativeGap));
      }
      if (s.objectiveBound != null) {
        parts.push("上界=" + this.formatEvNum(s.objectiveBound));
      }
      if (s.timeoutSeconds != null) {
        parts.push("时限=" + s.timeoutSeconds + "s");
      }
      if (s.solveMs != null) {
        parts.push("实耗=" + this.solveTimeText);
      }
      return parts.join("，");
    },

    solveExecutedAt() {
      const s = this.solveSummary;
      return s && s.executedAt ? s.executedAt : null;
    },

    solveOperator() {
      const s = this.solveSummary;
      return s && s.operator ? s.operator : null;
    },

    solveTimeText() {
      const s = this.solveSummary;
      const ms = s && s.solveMs;
      if (ms == null || ms < 0) {
        return "";
      }
      if (ms < 1000) {
        return ms + "ms";
      }
      if (ms < 60000) {
        return (ms / 1000).toFixed(2) + "s";
      }
      const min = Math.floor(ms / 60000);
      const sec = ((ms % 60000) / 1000).toFixed(1);
      return min + "min " + sec + "s";
    },

    filteredUnassignedOrders() {
      const list = (this.dayView && this.dayView.unassignedOrders) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredDispatchedTrips() {
      const list = (this.dayView && this.dayView.dispatchedTrips) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredAssignedPerson() {
      return this.filteredDispatchedTrips.reduce((sum, trip) => sum + (trip.assignedSeats || 0), 0);
    },

    filteredUnassignedPerson() {
      return this.filteredUnassignedOrders.reduce((sum, item) => sum + (item.number || 0), 0);
    },

    filteredTripCount() {
      return this.filteredDispatchedTrips.length;
    },

    filterPathStartArea() {
      if (!this.filterPathId) {
        return null;
      }
      const path = (this.pathOpts || []).find((item) => item.id === this.filterPathId);
      return path && path.startArea != null ? path.startArea : null;
    },

    vehicleFinalRows() {
      return this.filterVehicleTipsByPath(this.viewCache.vehicleFinal[this.viewDate] || []);
    },

    vehicleInitRows() {
      return this.filterVehicleTipsByPath(this.viewCache.vehicleInit[this.viewDate] || []);
    },

    vehicleFinalCount() {
      return this.vehicleFinalRows.length;
    },

    vehicleInitCount() {
      return this.vehicleInitRows.length;
    },

    hasTaskStatus() {
      if (this.running) {
        return true;
      }
      return !!(this.task && (this.task.status === 0 || this.task.status === 2));
    },

    initStartedAt() {
      return this.task && this.task.ctime ? this.task.ctime : null;
    },

    initFinishedAt() {
      return this.task && this.task.completeTime ? this.task.completeTime : null;
    },

    showInitProgress() {
      return this.running || !!(this.task && this.task.status === 0);
    },

    horizonStart() {
      if (this.showInitProgress && this.task && this.task.startDate) {
        return this.task.startDate;
      }
      return DateUtil.getDayStr(0, "yyyy-MM-dd");
    },

    horizonDays() {
      if (this.showInitProgress && this.task && this.task.days) {
        return this.task.days;
      }
      return 7;
    },

    wheelItems() {
      const base = this.horizonItems.length ? this.horizonItems : this.buildPlaceholderHorizon();
      return base.map((item) => this.decorateWheelItem(item));
    }
  },

  watch: {
    viewDate() {
      // 日期查询始终拉最新，不读本地 viewCache
      if (!this.viewDate) {
        this.dayView = null;
        this.solveSummary = null;
        this.sellableSnapshots = [];
        this.opAreaHourSnapshots = [];
        return;
      }
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.$delete(this.viewCache.plan, this.viewDate);
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.vehicleInit, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      this.lastLoadedQueryKey = null;
      this.syncFromCache();
      this.refreshAll();
    },
    filterPathId() {
      this.handleQueryChange(false);
    },
    tab() {
      this.$nextTick(() => this.updateTableMaxHeight());
    },
    horizonStart() {
      this.pollHorizon();
    }
  },

  mounted() {
    this.refreshAll();
    this.recoverRunningTask();
    this.startHorizonPoll();
    this.$nextTick(() => this.bindTableHeightObserver());
  },

  beforeDestroy() {
    this.stopPoll();
    this.stopHorizonPoll();
    this.stopOpsTestAuto();
    this.unbindTableHeightObserver();
  },

  methods: {
    formatEvNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 2);
    },

    formatGap(gap) {
      if (gap == null || Number.isNaN(Number(gap))) {
        return "";
      }
      return (Number(gap) * 100).toFixed(2) + "%";
    },

    bindTableHeightObserver() {
      const pane = this.getTabsContentEl();
      if (!pane || typeof ResizeObserver === "undefined") {
        this.updateTableMaxHeight();
        window.addEventListener("resize", this.updateTableMaxHeight);
        return;
      }
      this._tableHeightObserver = new ResizeObserver(() => {
        this.updateTableMaxHeight();
      });
      this._tableHeightObserver.observe(pane);
      this.updateTableMaxHeight();
    },

    unbindTableHeightObserver() {
      if (this._tableHeightObserver) {
        this._tableHeightObserver.disconnect();
        this._tableHeightObserver = null;
      }
      window.removeEventListener("resize", this.updateTableMaxHeight);
    },

    getTabsContentEl() {
      return this.$el && this.$el.querySelector(".el-tabs__content");
    },

    updateTableMaxHeight() {
      const pane = this.getTabsContentEl();
      if (!pane || !pane.clientHeight) {
        return;
      }
      const activePane = this.$el.querySelector(`#pane-${this.tab}`) || pane;
      const root = activePane.querySelector(".root-box");
      let chrome = 0;
      if (root) {
        const cs = window.getComputedStyle(root);
        chrome += (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      }
      const footer = activePane.querySelector(".page");
      if (footer) {
        const fcs = window.getComputedStyle(footer);
        // display:none 的页签 offsetHeight 为 0，用 28 兜底
        chrome += Math.max(footer.offsetHeight, 28)
          + (parseFloat(fcs.marginTop) || 0)
          + (parseFloat(fcs.marginBottom) || 0);
      }
      this.tableMaxHeight = Math.max(160, Math.floor(pane.clientHeight - chrome));
    },

    showTaskError() {
      if (!this.task || !this.task.errorMsg) {
        return;
      }
      this.$alert(this.task.errorMsg, "初始化失败", {
        confirmButtonText: "关闭",
        customClass: "ev-task-error-alert"
      });
    },

    normalizeOpAreaHourView(data) {
      if (Array.isArray(data)) {
        return { snapshots: data };
      }
      if (!data) {
        return { snapshots: [] };
      }
      return { snapshots: data.snapshots || [] };
    },

    isQueryCached() {
      if (!this.viewDate) {
        return false;
      }
      if (!this.viewCache.plan[this.viewDate]) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.solveSummary, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.vehicleFinal, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.vehicleInit, this.viewDate)) {
        return false;
      }
      if (!this.filterPathId) {
        return true;
      }
      const key = this.pathCacheKey;
      return key && Object.prototype.hasOwnProperty.call(this.viewCache.sellable, key)
        && Object.prototype.hasOwnProperty.call(this.viewCache.opAreaHour, key);
    },

    handleQueryChange(forcePathRefresh) {
      this.syncFromCache();
      const key = this.queryKey;
      if (!forcePathRefresh && key === this.lastLoadedQueryKey) {
        return;
      }
      if (!forcePathRefresh && this.isQueryCached()) {
        this.lastLoadedQueryKey = key;
        return;
      }
      if (!this.viewDate) {
        return;
      }
      this.refreshAll();
    },

    invalidatePathViewCache() {
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.lastLoadedQueryKey = null;
    },

    filterVehicleTipsByPath(rows) {
      const list = rows || [];
      if (!this.filterPathId) {
        return list;
      }
      const startArea = this.filterPathStartArea;
      if (startArea == null) {
        return [];
      }
      return list.filter((row) => row && row.areaId === startArea);
    },

    syncFromCache() {
      this.dayView = this.viewCache.plan[this.viewDate] || null;
      this.solveSummary = this.viewCache.solveSummary[this.viewDate] || null;
      const key = this.pathCacheKey;
      this.sellableSnapshots = key ? (this.viewCache.sellable[key] || []) : [];
      const opCached = key ? this.viewCache.opAreaHour[key] : null;
      this.opAreaHourSnapshots = this.normalizeOpAreaHourView(opCached).snapshots;
    },

    onRefresh() {
      if (!this.viewDate) {
        this.$message.warning("请选择日期");
        return;
      }
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.vehicleInit, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      this.lastLoadedQueryKey = null;
      this.refreshAll();
    },

    refreshAll() {
      if (!this.viewDate) {
        return Promise.resolve();
      }
      this.refreshing = true;
      const planReq = getEvPlanView({ date: this.viewDate, _hideLoading: true });
      const summaryReq = getEvSolveSummaryView({ date: this.viewDate, _hideLoading: true });
      const vehicleFinalReq = getEvVehicleFinalView({ date: this.viewDate, _hideLoading: true });
      const vehicleInitReq = getEvVehicleInitView({ date: this.viewDate, _hideLoading: true });
      const pathKey = this.pathCacheKey;
      const pathReqs = pathKey
        ? [
          getEvSellableView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true }),
          getEvOpAreaHourView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true })
        ]
        : [];

      return Promise.all([planReq, summaryReq, vehicleFinalReq, vehicleInitReq, ...pathReqs])
        .then(([planRes, summaryRes, vehicleFinalRes, vehicleInitRes, ...pathResults]) => {
          this.$set(this.viewCache.plan, this.viewDate, planRes.data);
          this.dayView = planRes.data;
          const summary = summaryRes.data || null;
          this.$set(this.viewCache.solveSummary, this.viewDate, summary);
          this.solveSummary = summary;
          this.$set(this.viewCache.vehicleFinal, this.viewDate, vehicleFinalRes.data || []);
          this.$set(this.viewCache.vehicleInit, this.viewDate, vehicleInitRes.data || []);
          if (!pathKey) {
            this.sellableSnapshots = [];
            this.opAreaHourSnapshots = [];
            return;
          }
          const [sellableRes, opAreaHourRes] = pathResults;
          const sellable = sellableRes.data || [];
          const opView = this.normalizeOpAreaHourView(opAreaHourRes.data);
          this.$set(this.viewCache.sellable, pathKey, sellable);
          this.$set(this.viewCache.opAreaHour, pathKey, opView.snapshots);
          this.sellableSnapshots = sellable;
          this.opAreaHourSnapshots = opView.snapshots;
        })
        .finally(() => {
          this.refreshing = false;
          this.lastLoadedQueryKey = this.queryKey;
        });
    },

    startHorizonPoll() {
      this.stopHorizonPoll();
      this.pollHorizon();
      this.horizonTimer = setInterval(() => this.pollHorizon(), 3000);
    },

    stopHorizonPoll() {
      if (this.horizonTimer) {
        clearInterval(this.horizonTimer);
        this.horizonTimer = null;
      }
    },

    pollHorizon() {
      getEvSolverHorizon({
        startDate: this.horizonStart,
        days: this.horizonDays
      }).then((res) => {
        const data = (res && res.data) || {};
        this.horizonItems = (data.items || []).map((row) => ({
          date: this.normalizeHorizonDate(row && row.date),
          state: row.state || "idle",
          solverAlive: !!row.solverAlive,
          queued: !!row.queued,
          initInFlight: !!row.initInFlight,
          initHorizon: !!row.initHorizon
        }));
        this.horizonItems.forEach((row) => {
          if (!row.date) {
            return;
          }
          this.$set(this.liveEpochByDate, row.date, {
            solverAlive: row.solverAlive,
            queued: row.queued,
            initInFlight: row.initInFlight,
            initHorizon: row.initHorizon
          });
        });
      }).catch(() => { });
    },

    buildPlaceholderHorizon() {
      const start = this.horizonStart;
      const n = this.horizonDays || 7;
      const list = [];
      for (let i = 0; i < n; i++) {
        const date = this.addHorizonDays(start, i);
        if (date) {
          list.push({ date, state: "idle" });
        }
      }
      return list;
    },

    addHorizonDays(dateStr, n) {
      const raw = String(dateStr || "").replace(/-/g, "/");
      const d = new Date(raw);
      if (isNaN(d.getTime())) {
        return "";
      }
      d.setDate(d.getDate() + n);
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return d.getFullYear() + "-" + (m < 10 ? "0" + m : m) + "-" + (day < 10 ? "0" + day : day);
    },

    normalizeHorizonDate(value) {
      if (value == null || value === "") {
        return "";
      }
      if (Array.isArray(value) && value.length >= 3) {
        const m = String(value[1]).padStart(2, "0");
        const d = String(value[2]).padStart(2, "0");
        return value[0] + "-" + m + "-" + d;
      }
      if (typeof value === "object") {
        const y = value.year;
        const m = value.monthValue || value.month;
        const d = value.dayOfMonth || value.day;
        if (y && m && d) {
          return y + "-" + String(m).padStart(2, "0") + "-" + String(d).padStart(2, "0");
        }
      }
      const s = String(value);
      const hit = s.match(/(\d{4})-(\d{2})-(\d{2})/);
      return hit ? hit[1] + "-" + hit[2] + "-" + hit[3] : s;
    },

    onEpochChange(doc) {
      const date = this.normalizeHorizonDate(doc && doc.date);
      if (!date) {
        return;
      }
      this.$set(this.liveEpochByDate, date, {
        solverAlive: !!doc.solverAlive,
        queued: !!doc.queued,
        initInFlight: !!doc.initInFlight,
        initHorizon: !!doc.initHorizon
      });
    },

    decorateWheelItem(item) {
      const date = this.normalizeHorizonDate(item && item.date);
      const live = this.liveEpochByDate[date] || {};
      const solverAlive = !!(item && item.solverAlive) || !!live.solverAlive;
      const queued = !!(item && item.queued) || !!live.queued;
      const initInFlight = !!(item && item.initInFlight) || !!live.initInFlight;
      const initHorizon = !!(item && item.initHorizon) || !!live.initHorizon;
      let state = (item && item.state) || "idle";
      if (initInFlight || solverAlive) {
        state = "running";
      } else if (queued) {
        state = "queued";
      } else if (initHorizon && (state === "idle" || state === "pending")) {
        state = "paused";
      }
      const next = Object.assign({}, item, {
        date,
        state,
        solverAlive,
        queued,
        initInFlight,
        initHorizon
      });
      if (this.showInitProgress && this.task) {
        return this.overlayInitState(next);
      }
      return next;
    },

    overlayInitState(item) {
      const date = item && item.date;
      if (!date || !this.task) {
        return item;
      }
      const inflight = this.task.inFlightDates || [];
      if (inflight.indexOf(date) >= 0) {
        return Object.assign({}, item, { state: "running" });
      }
      if (item.queued) {
        return Object.assign({}, item, { state: "queued" });
      }
      const rows = this.task.dayResults || [];
      const row = rows.find((r) => r && r.date === date);
      if (row) {
        return Object.assign({}, item, { state: row.error ? "failed" : "done" });
      }
      if (item.state === "idle" || item.state === "paused") {
        return Object.assign({}, item, { state: "pending" });
      }
      return item;
    },

    recoverRunningTask() {
      this.recoverFromStore();
      getEvRunningSimulate()
        .then((res) => {
          const task = res && res.data;
          if (task && task.status === 0) {
            this.attachRunningTask(task);
            return;
          }
          if (!this.taskId) {
            this.clearStoredTaskId();
          }
        })
        .catch(() => { });
    },

    recoverFromStore() {
      const id = this.readStoredTaskId();
      if (!id) {
        return;
      }
      getEvSimulateTask(id).then((res) => {
        const task = res && res.data;
        if (task && task.status === 0) {
          this.attachRunningTask(task);
        } else {
          this.clearStoredTaskId();
        }
      });
    },

    onWheelSelectDate(date) {
      if (date) {
        this.viewDate = date;
      }
    },

    readStoredTaskId() {
      try {
        return localStorage.getItem(RUNNING_TASK_KEY);
      } catch (e) {
        return null;
      }
    },

    saveStoredTaskId(id) {
      try {
        if (id) {
          localStorage.setItem(RUNNING_TASK_KEY, id);
        } else {
          localStorage.removeItem(RUNNING_TASK_KEY);
        }
      } catch (e) { /* ignore */ }
    },

    clearStoredTaskId() {
      this.saveStoredTaskId(null);
    },

    attachRunningTask(task) {
      if (!task || !task.id) {
        return;
      }
      this.task = task;
      this.taskId = task.id;
      this.running = true;
      this.getResulted = true;
      this.saveStoredTaskId(task.id);
      if (task.startDate) {
        this.param.startDate = task.startDate;
      }
      if (task.days) {
        this.param.days = task.days;
      }
      this.startPoll();
    },

    startPoll() {
      this.stopPoll();
      this.pollTask();
      this.pollTimer = setInterval(() => this.pollTask(), 3000);
    },

    forceStopSimulate() {
      this.$confirm("将打断当前初始化并清掉闸门，之后可以重新点初始化。确认停止？", "强制停止", {
        type: "warning"
      }).then(() => {
        this.stopping = true;
        return forceStopEvSimulate();
      }).then(() => {
        this.running = false;
        this.stopping = false;
        this.getResulted = true;
        this.clearStoredTaskId();
        this.stopPoll();
        if (this.task) {
          this.task = Object.assign({}, this.task, {
            status: 2,
            errorMsg: "已强制停止",
            completeTime: this.task.completeTime || null,
            inFlightDates: []
          });
        }
        this.$message.success("已强制停止，可以重新初始化");
      }).catch((err) => {
        this.stopping = false;
        if (err === "cancel" || err === "close") {
          return;
        }
      });
    },

    startSimulate() {
      if (!this.param.startDate) {
        this.$message.error("请选择开始推导日期");
        return;
      }
      this.$confirm(
        `将按 ${this.param.startDate} 起共 ${this.param.days} 天重新初始化，确认继续？`,
        "初始化",
        { type: "warning" }
      ).then(() => {
        this.stopPoll();
        this.taskId = null;
        this.running = true;
        this.getResulted = false;
        this.task = {
          status: 0,
          currentDay: 0,
          totalDays: this.param.days || 0,
          startDate: this.param.startDate,
          days: this.param.days,
          inFlightDates: []
        };

        submitEvSimulate({
          startDate: this.param.startDate,
          days: this.param.days,
          _hideLoading: true
        })
          .then((res) => {
            this.getResulted = true;
            this.taskId = res.data.taskId;
            this.saveStoredTaskId(this.taskId);
            this.startPoll();
          })
          .catch(() => {
            this.running = false;
            this.getResulted = true;
            this.task = null;
            this.recoverRunningTask();
          });
      }).catch(() => { });
    },

    pollTask() {
      if (!this.taskId || !this.getResulted) return;

      getEvSimulateTask(this.taskId).then((res) => {
        this.task = res.data;
        if (this.task.status === 1) {
          this.running = false;
          this.clearStoredTaskId();
          this.stopPoll();
          this.viewCache.opAreaHour = {};
          this.viewCache.sellable = {};
          this.viewCache.vehicleFinal = {};
          this.viewCache.vehicleInit = {};
          this.viewCache.solveSummary = {};
          this.viewCache.plan = {};
          this.lastLoadedQueryKey = null;
          const sameDate = this.viewDate === this.param.startDate;
          this.viewDate = this.param.startDate;
          // 日期未变时 watcher 不会触发，需主动刷新；日期变了由 viewDate watcher 拉最新
          if (sameDate) {
            this.refreshAll();
          }
          this.$message.success("初始化完成，数据已刷新");
        } else if (this.task.status === 2) {
          this.running = false;
          this.clearStoredTaskId();
          this.stopPoll();
        }
      });
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    goConfig() {
      this.$router.push("/work_common/ev_simulate/config");
    },

    onMoreCommand(cmd) {
      if (cmd === "noop") {
        return;
      }
      if (this.$refs.moreDropdown && this.$refs.moreDropdown.hide) {
        this.$refs.moreDropdown.hide();
      }
      if (cmd === "opsTest") {
        this.openOpsTest();
        return;
      }
      if (cmd === "config") {
        this.goConfig();
      }
    },

    openOpsTest() {
      this.$refs.opsTestDialog.open();
    },

    onOpsTestAutoChange(on) {
      this.stopOpsTestAuto();
      if (!on) {
        this.$message.info("已关闭操作测试自动执行");
        return;
      }
      this.$message.success("已开启操作测试自动执行，每 3 分钟一次");
      this.runOpsTestOnce();
      this.opsTestAutoTimer = setInterval(() => this.runOpsTestOnce(), 3 * 60 * 1000);
    },

    stopOpsTestAuto() {
      if (this.opsTestAutoTimer) {
        clearInterval(this.opsTestAutoTimer);
        this.opsTestAutoTimer = null;
      }
    },

    runOpsTestOnce() {
      const dialog = this.$refs.opsTestDialog;
      if (dialog && typeof dialog.startIfIdle === "function") {
        dialog.startIfIdle();
      }
    },

    onOpsTestDone() {
      this.onRefresh();
    },

    refreshAfterPlanChange() {
      if (!this.viewDate) {
        return Promise.resolve();
      }
      this.$delete(this.viewCache.plan, this.viewDate);
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.lastLoadedQueryKey = null;
      return this.refreshAll();
    },

    formatAssignMsg(action, data) {
      const driverNo = data && data.driverNo ? data.driverNo : "-";
      const showTime = data && data.showTime ? data.showTime : "";
      return `${action}成功：司机 ${driverNo}${showTime ? " / " + showTime : ""}`;
    },

    handleAssignOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$refs.assignDialog.open(order, false);
    },

    handleRevokeOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$confirm("确认撤回该订单的规划绑定？", "撤回", {
        type: "warning"
      }).then(() => {
        return revokeEvOrder(order.id);
      }).then(() => {
        this.$message.success("已撤回规划绑定");
        return this.refreshAfterPlanChange();
      }).catch(() => { });
    },

    handleReassignOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$refs.assignDialog.open(order, true);
    },

    onAssignSuccess({ action, data }) {
      this.$message.success(this.formatAssignMsg(action, data));
      this.refreshAfterPlanChange();
    }
  }
};
</script>

<style lang="less" scoped>
.ev-simulate-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 6px 12px 8px;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  flex-shrink: 0;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;

}

.header-main {
  display: flex;
  align-items: stretch;
  gap: 12px;
  min-height: 36px;
}

.header-block {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.block-label {
  font-size: 11px;
  color: #909399;
  writing-mode: vertical-rl;
  letter-spacing: 2px;
  margin-right: 8px;
  flex-shrink: 0;
  user-select: none;
}

.derive-block,
.view-block {
  padding: 4px 10px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.more-btn.is-auto {
  position: relative;
}

.more-btn.is-auto::after {
  content: "";
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #67c23a;
}

.ops-auto-item {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  min-width: 148px;
}

.ev-block-wrap {
  flex: 1;
  min-width: 0;
  display: flex;

  /deep/ .el-tooltip {
    flex: 1;
    min-width: 0;
    display: flex;
    width: 100%;
  }
}

.ev-block {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f0f7ff 0%, #fafcff 100%);
  border: 1px solid #d9ecff;
  border-radius: 4px;
  min-width: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c0c4cc;
    border-radius: 2px;
  }
}

.ev-placeholder {
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

.ev-placeholder-text {
  font-size: 12px;
  color: #c0c4cc;
  width: 100%;
  text-align: center;
}

.ev-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 0 6px;
  border-right: 1px solid #e4e7ed;

  &:last-child {
    border-right: none;
  }
}

.ev-metric-label {
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  line-height: 1.4;
}

.ev-metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;

  &.muted {
    color: #606266;
    font-size: 13px;
  }
}

.ev-metric.is-penalty .ev-metric-value {
  color: #f56c6c;
}

.ev-metric.is-objective .ev-metric-value {
  color: #409eff;
  font-size: 14px;
}

.ev-metric.is-objective.is-mismatch .ev-metric-value {
  color: #f56c6c;
  text-decoration: underline;
}

.ev-metric.is-reconcile .ev-metric-value {
  color: #e6a23c;
}

.ev-metric.is-unassigned .ev-metric-value {
  color: #f56c6c;
}

.ev-metric.is-warn .ev-metric-value {
  color: #e6a23c;
}

.ev-metric.is-ok .ev-metric-value {
  color: #67c23a;
}

.ev-metric-unit {
  font-size: 11px;
  font-weight: 500;
}

.ev-metric-gap {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
}

.task-status {
  display: flex;
  align-items: center;
}

.status-text {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.toolbar__done {
  font-size: 12px;
  color: #67c23a;
  white-space: nowrap;
}

.toolbar__error {
  font-size: 12px;
  color: #f56c6c;
  max-width: 420px;
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
  cursor: pointer;
}

.compact-form {
  /deep/ .el-form-item {
    margin-bottom: 0;
    margin-right: 8px;
  }

  /deep/ .el-form-item__label {
    padding-right: 4px;
    font-size: 12px;
  }
}

.derive-block,
.view-block {
  /deep/ .el-form {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
  }
}

.main-content {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tabs-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  height: 40px;
}

.tel-search {
  width: 180px;
}

.fill-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /deep/ .el-tabs__header {
    margin-bottom: 6px;
    padding-right: 190px;
  }

  /deep/ .el-tabs__content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /deep/ .el-tab-pane {
    height: 100%;
    overflow: hidden;
  }

  /* 覆盖 global.less：.root-box padding 10px、.page margin-top 10px，避免把汇总行挤出可视区 */
  /deep/ .root-box {
    height: 100%;
    padding: 0 0 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  /deep/ .root-box>.page {
    flex-shrink: 0;
    margin-top: 0;
    box-sizing: border-box;
    height: 28px;
    padding-top: 4px;
    line-height: 20px;
  }
}

.idle-empty-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 13px;
}
</style>
